/**
 * Spreadsheet Builder Page.
 * Permet aux utilisateurs de créer des feuilles de calcul à partir de demandes en langage naturel.
 */

import React, { useState, useCallback } from 'react';
import { Button, Text, Input } from '@fluentui/react-components';
import { SparkleRegular, ArrowResetRegular } from '@fluentui/react-icons';
import type { SpreadsheetPlan } from '@excel-ai-studio/shared';
import { generateSpreadsheetPlan } from '../services/api.client';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { SpreadsheetPreviewPanel } from '../components/features/SpreadsheetPreviewPanel';
import { createSpreadsheetFromPlan } from '../office/spreadsheet-creator';
import './SpreadsheetBuilderPage.css';

export const SpreadsheetBuilderPage: React.FC = () => {
  // Step 1: Prompt input
  const [prompt, setPrompt] = useState('');

  // Step 2: Plan generation
  const [plan, setPlan] = useState<SpreadsheetPlan>();
  const [planLoading, setPlanLoading] = useState(false);

  // Step 3: Creation
  const [creationLoading, setCreationLoading] = useState(false);

  // Shared
  const [error, setError] = useState<string>();

  // ─── Helpers ─────────────────────────────────────────

  const resetAll = () => {
    setPrompt('');
    setPlan(undefined);
    setError(undefined);
  };

  // ─── Step 1: Input prompt ────────────────────────────

  const handleGeneratePlan = useCallback(async () => {
    if (!prompt.trim()) {
      setError('Veuillez entrer une description de la feuille.');
      return;
    }

    setError(undefined);
    setPlanLoading(true);

    try {
      const response = await generateSpreadsheetPlan({
        prompt: prompt.trim(),
      });

      setPlan(response.plan);
    } catch (err) {
      setError((err as Error).message);
      setPlan(undefined);
    } finally {
      setPlanLoading(false);
    }
  }, [prompt]);

  // ─── Step 2: Create spreadsheet ──────────────────────

  const handleCreateSpreadsheet = useCallback(async () => {
    if (!plan) return;

    setError(undefined);
    setCreationLoading(true);

    try {
      await createSpreadsheetFromPlan(plan);
      resetAll();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreationLoading(false);
    }
  }, [plan]);

  // ─── Render ──────────────────────────────────────────

  return (
    <div className="spreadsheet-builder-page">
      {/* Error Display */}
      {error && <ErrorDisplay message={error} />}

      {/* Step 1: Input Prompt */}
      {!plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 1: Décrire la feuille
          </Text>
          <Text size={300}>
            Décrivez le type de feuille que vous souhaitez créer en langage naturel.
          </Text>
          <div className="prompt-input-group">
            <Input
              placeholder="Ex: Crée un suivi de dépenses, Crée un tableau de gestion des employés..."
              value={prompt}
              onChange={(_, data) => setPrompt(data.value)}
              className="prompt-input"
            />
            <Button
              appearance="primary"
              size="large"
              icon={<SparkleRegular />}
              onClick={handleGeneratePlan}
              disabled={!prompt.trim()}
            >
              {planLoading ? 'Génération en cours...' : 'Générer'}
            </Button>
          </div>
          {planLoading && <LoadingSpinner />}
        </div>
      )}

      {/* Step 2: Preview and Create */}
      {plan && (
        <div className="step-container">
          <Text weight="semibold" size={400}>
            Étape 2: Aperçu et création
          </Text>
          <SpreadsheetPreviewPanel
            plan={plan}
            onGenerate={handleCreateSpreadsheet}
            isGenerating={creationLoading}
          />
          <Button
            appearance="secondary"
            size="large"
            icon={<ArrowResetRegular />}
            onClick={resetAll}
            disabled={creationLoading}
          >
            Recommencer
          </Button>
        </div>
      )}
    </div>
  );
};
